import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import format from 'date-fns/format'
import { kebabCase } from 'lodash-es'
import GatsbyImage from 'gatsby-image'
import { Location } from '@reach/router'

import siteConfig from '../../site-config'

import useScrolledBelow from '../hooks/useScrolledBelow'

import theme from '../styles/theme'
import { rem, media, min } from '../styles/tools'
import { useNavbarTheme } from '../contexts/NavbarTheme'

import SEO from '../components/SEO'
import Space from '../components/Space'
import Shell from '../components/Shell'
import Wrapper from '../components/Wrapper'
import { ContentAboveFooter } from '../components/Footer'
import HelloFab from '../components/HelloFab'
import NewsletterFooter from '../components/NewsletterFooter'
import NewsletterForm from '../components/NewsletterForm'
import Link from '../components/Link'
import TextUnderline from '../components/TextUnderline'
import { Content, HTMLContent } from '../components/Content'
import {
  Detail,
  Headline,
  Paragraph,
  H2,
  postStyles,
  wideTextStyles,
  WideText,
} from '../styles/typography'

const PostTile = ({ to, image, children, ...props }) => (
  <Link
    to={to}
    css={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: 'calc(20vh - 20px)',
      [media.tablet]: { height: 'calc(40vh - 40px)' },
      position: 'relative',
      zIndex: 0,
      overflow: 'hidden',
      ':hover > .gatsby-image-wrapper': {
        opacity: 0.6,
        transform: 'scale(1.5)',
      },
    }}
    {...props}
  >
    <GatsbyImage
      {...image}
      css={{
        width: '100%',
        height: '100%',
        position: 'absolute !important',
        zIndex: -1,
        top: 0,
        left: 0,
        opacity: 0,
        transition: ' opacity 1s, transform 5s',
      }}
    />
    {children}
  </Link>
)

const SocialIcon = ({ icon, size = 32, ...props }) => {
  const Icon = {
    facebook: props => (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M5,3H19A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3M18,5H15.5A3.5,3.5 0 0,0 12,8.5V11H10V14H12V21H15V14H18V11H15V9A1,1 0 0,1 16,8H18V5Z" />
      </svg>
    ),
    linkedIn: props => (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z" />
      </svg>
    ),
    twitter: props => (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M5,3H19A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3M17.71,9.33C18.19,8.93 18.75,8.45 19,7.92C18.59,8.13 18.1,8.26 17.56,8.33C18.06,7.97 18.47,7.5 18.68,6.86C18.16,7.14 17.63,7.38 16.97,7.5C15.42,5.63 11.71,7.15 12.37,9.95C9.76,9.79 8.17,8.61 6.85,7.16C6.1,8.38 6.75,10.23 7.64,10.74C7.18,10.71 6.83,10.57 6.5,10.41C6.54,11.95 7.39,12.69 8.58,13.09C8.22,13.16 7.82,13.18 7.44,13.12C7.81,14.19 8.58,14.86 9.9,15C9,15.76 7.34,16.29 6,16.08C7.15,16.81 8.46,17.39 10.28,17.31C14.69,17.11 17.64,13.95 17.71,9.33Z" />
      </svg>
    ),
    mail: props => (
      <svg viewBox="0 0 24 24" {...props}>
        <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
      </svg>
    ),
  }[icon]

  if (!Icon) throw new Error('Invalid icon name')

  return <Icon width={size} height={size} fill="currentColor" {...props} />
}

const ShareIcons = ({ title, ...props }) => {
  const isBelow = useScrolledBelow(50)

  return (
    <Location>
      {({ location: { pathname } }) => {
        const shareUrl = siteConfig.siteUrl + pathname

        return (
          <ul
            style={{ color: !isBelow && '#bbb' }}
            css={{
              gridColumn: '2 / 3',
              alignSelf: 'start',
              display: 'grid',
              gridAutoFlow: 'column',
              justifyContent: 'start',
              alignItems: 'center',
              gridGap: rem(30),
              marginTop: rem(80),
              [media.tabletMd]: {
                gridAutoFlow: 'row',
                gridColumn: '3 / 4',
                gridGap: rem(15),
                position: 'sticky',
                top: theme.navbarHeight.mobile,
                marginTop: rem(-30),
                paddingTop: rem(30),
                paddingLeft: rem(15),
              },
              [media.laptop]: {
                top: theme.navbarHeight.laptop,
              },
              a: {
                transition: '.3s',
                ':hover': {
                  color: 'var(--hover-color, #888)',
                },
              },
            }}
            {...props}
          >
            <li>
              <Link
                out
                href={`https://www.facebook.com/sharer.php?u=${shareUrl}`}
                css={{ '--hover-color': '#3b5998' }}
              >
                <SocialIcon icon="facebook" />
              </Link>
            </li>
            <li>
              <Link
                out
                href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${title}`}
                css={{ '--hover-color': '#1da1f2' }}
              >
                <SocialIcon icon="twitter" />
              </Link>
            </li>
            <li>
              <Link
                out
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`}
                css={{ '--hover-color': '#0077b5' }}
              >
                <SocialIcon icon="linkedIn" />
              </Link>
            </li>
            <li>
              <Link href={`mailto:?subject=${title}&body=${shareUrl}`}>
                <SocialIcon icon="mail" height={29} />
              </Link>
            </li>
          </ul>
        )
      }}
    </Location>
  )
}

export const BlogPostTemplate = ({
  title,
  readingTime,
  date,
  description,
  contentComponent: PostContent = Content,
  content,
  tags,
}) => (
  <Fragment>
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 8fr 1fr',
        [media.tabletMd]: { gridTemplateColumns: '1fr 4fr 1fr' },
      }}
    >
      <div css={{ gridColumn: '2 / 3' }}>
        <Headline as="h1">{title || 'Title...'}</Headline>
        <Space y={30} />
        <div
          css={{
            [min(1200)]: { width: 'calc(75% - 1px)' },
          }}
        >
          <H2>{description || 'Subtitle...'}</H2>
          <Space y={30} />
          <small
            css={{
              fontWeight: 700,
              fontSize: rem(16),
              [media.tabletMd]: { fontSize: rem(18) },
            }}
          >
            <span css={{ color: theme.colors.teal }}>
              {readingTime || 'Reading time'}
            </span>
            <span css={{ color: theme.colors.grey_200 }}>
              {' '}
              • {format(date, 'MMMM DD, YYYY')}
            </span>
          </small>
          <Space y={60} />
          {content ? (
            <PostContent content={content} css={postStyles} />
          ) : (
            <Paragraph>Content...</Paragraph>
          )}
          <Space y={60} />
          {tags && tags.length && (
            <div>
              <Paragraph as="span" css={{ marginRight: rem(10) }}>
                Tagged with
              </Paragraph>
              <ul
                css={{
                  display: 'inline-flex',
                  flexWrap: 'wrap',
                }}
              >
                {tags.map((tag, i) => (
                  <li key={`${tag}tag`}>
                    {i !== 0 && (
                      <span
                        aria-hidden
                        css={{ ...wideTextStyles, marginRight: rem(5) }}
                      >
                        |
                      </span>
                    )}
                    <WideText
                      as={Link}
                      to={`/blog/tags/${kebabCase(tag)}/`}
                      css={{ marginRight: rem(5) }}
                    >
                      {tag}
                    </WideText>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Space y={90} />
          <NewsletterForm
            defaultMessage={() => (
              <H2 as="p" css={{ marginBottom: rem(50) }}>
                Enjoyed this article? Receive the next one in your inbox!
              </H2>
            )}
            successMessage={() => (
              <H2 as="p" css={{ marginBottom: rem(50) }}>
                Thank you for signing up for our newsletter. We promise no spam,
                just good stuff! <span aria-hidden>👏👏</span>
              </H2>
            )}
            errorMessage={message => (
              <H2
                as="p"
                css={{ marginBottom: rem(50) }}
                dangerouslySetInnerHTML={{ __html: message }}
              />
            )}
          />
        </div>
      </div>
      <ShareIcons title={title} />
    </div>
  </Fragment>
)

BlogPostTemplate.propTypes = {
  title: PropTypes.string,
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.string,
  contentComponent: PropTypes.func,
  content: PropTypes.node,
  tags: PropTypes.array,
}

export const BlogPostPreview = ({ entry, widgetFor }) => {
  let tags = entry.getIn(['data', 'tags'])

  // Make sure tags filed exist and make sure it's an array
  // because Preview component requires an array
  tags = tags ? Array.from(tags) : tags

  return (
    <Wrapper light>
      <Space y={100} />
      <BlogPostTemplate
        title={entry.getIn(['data', 'title'])}
        date={entry.getIn(['data', 'date'])}
        description={entry.getIn(['data', 'description'])}
        content={widgetFor('body')}
        tags={tags}
      />
      <Space y={100} />
    </Wrapper>
  )
}

BlogPostPreview.propTypes = {
  entry: PropTypes.shape({
    getIn: PropTypes.func,
  }),
  widgetFor: PropTypes.func,
}

export const pageQuery = graphql`
  query BlogPostByID($currentId: String!, $prevId: String!, $nextId: String!) {
    currentPost: markdownRemark(id: { eq: $currentId }) {
      id
      html
      frontmatter {
        title
        date
        description
        tags
        facebookImage: thumbnail {
          ...facebookShareImage
        }
        twitterImage: thumbnail {
          ...twitterShareImage
        }
      }
      fields {
        readingTime {
          text
        }
      }
    }
    prevPost: markdownRemark(id: { eq: $prevId }) {
      fields {
        slug
      }
      frontmatter {
        title
        thumbnail {
          ...textContainerWide
        }
      }
    }
    nextPost: markdownRemark(id: { eq: $nextId }) {
      fields {
        slug
      }
      frontmatter {
        title
        thumbnail {
          ...textContainerWide
        }
      }
    }
  }
`

const BlogPost = ({ data: { currentPost, prevPost, nextPost } }) => {
  useNavbarTheme('light')

  return (
    <Fragment>
      <SEO
        title={currentPost.frontmatter.title}
        description={currentPost.frontmatter.description}
        facebookImage={
          currentPost.frontmatter.facebookImage.childImageSharp.fixed.src
        }
        twitterImage={
          currentPost.frontmatter.twitterImage.childImageSharp.fixed.src
        }
      />
      <Shell>
        <ContentAboveFooter>
          <Wrapper light>
            <Space y={{ mobile: 100, tabletMd: 150 }} />
            <BlogPostTemplate
              content={currentPost.html}
              contentComponent={HTMLContent}
              description={currentPost.frontmatter.description}
              readingTime={currentPost.fields.readingTime.text}
              date={currentPost.frontmatter.date}
              tags={currentPost.frontmatter.tags}
              title={currentPost.frontmatter.title}
            />
            <Space y={100} />
          </Wrapper>
          <div
            css={{
              display: 'grid',
              [media.tablet]: { gridTemplateColumns: '1fr 1fr' },
            }}
          >
            <PostTile
              to={`/${siteConfig.blogPathname}/${prevPost.fields.slug}`}
              image={{
                fluid: prevPost.frontmatter.thumbnail.childImageSharp.fluid,
              }}
              css={{ backgroundColor: theme.colors.grey_900 }}
            >
              <Detail css={{ display: 'block', marginBottom: rem(10) }}>
                Previous post
              </Detail>
              <H2 as="h3" css={{ width: '70%', textAlign: 'center' }}>
                <TextUnderline top={0.74} color="rgba(255, 255, 255, .3)">
                  {prevPost.frontmatter.title}
                </TextUnderline>
              </H2>
            </PostTile>
            <PostTile
              to={`/${siteConfig.blogPathname}/${nextPost.fields.slug}`}
              image={{
                fluid: nextPost.frontmatter.thumbnail.childImageSharp.fluid,
              }}
              css={{ backgroundColor: theme.colors.grey_1000 }}
            >
              <Detail css={{ display: 'block', marginBottom: rem(10) }}>
                Next post
              </Detail>
              <H2 as="h3" css={{ width: '70%', textAlign: 'center' }}>
                <TextUnderline top={0.74} color="rgba(255, 255, 255, .3)">
                  {nextPost.frontmatter.title}
                </TextUnderline>
              </H2>
            </PostTile>
          </div>
        </ContentAboveFooter>
        <NewsletterFooter />
        <HelloFab />
      </Shell>
    </Fragment>
  )
}

BlogPost.propTypes = {
  data: PropTypes.shape({
    currentPost: PropTypes.object,
    prevPost: PropTypes.object,
    nextPost: PropTypes.object,
  }),
}

export default BlogPost
